import { getApiErrorBody, getErrorMessages } from "./StringUtils";
import { getPeriodClosedError } from "./PeriodUtils";
import { getHasPaymentsMessage, getInvoiceBalance } from "./PaymentUtils";

// Sales deposits (uang muka penjualan): money a customer pays before the goods
// or service are delivered. It is a liability (220205 Uang Muka Pelanggan)
// until it is applied to an invoice, refunded or forfeited.

export const DEPOSIT_ACCOUNT_CODE = "220205";

// Status is always text and colour together, never colour alone.
export const DEPOSIT_STATUS = {
  OPEN: {
    label: "deposits.status.OPEN",
    className: "bg-blue-100 text-blue-800",
  },
  PARTIAL: {
    label: "deposits.status.PARTIAL",
    className: "bg-amber-100 text-amber-800",
  },
  USED: {
    label: "deposits.status.USED",
    className: "bg-green-100 text-green-800",
  },
  REFUNDED: {
    label: "deposits.status.REFUNDED",
    className: "bg-gray-100 text-gray-700",
  },
  FORFEITED: {
    label: "deposits.status.FORFEITED",
    className: "bg-gray-100 text-gray-700",
  },
};

export const DEPOSIT_STATUS_ORDER = [
  "OPEN",
  "PARTIAL",
  "USED",
  "REFUNDED",
  "FORFEITED",
];

// A deposit that still has something left to apply, refund or forfeit.
export const isDepositOpen = (deposit) =>
  ["OPEN", "PARTIAL"].includes(deposit?.status) &&
  (Number(deposit?.remaining) || 0) > 0;

export const DEPOSIT_TRANSACTION_TYPES = [
  "SALES_DEPOSIT",
  "SALES_DEPOSIT_APPLICATION",
  "SALES_DEPOSIT_REFUND",
  "SALES_DEPOSIT_FORFEIT",
];

// These rows are written by /deposit and refused by /transaction, so every list
// that offers Edit/Delete on a transaction has to route them to the deposit.
export const isDepositTransaction = (transaction) =>
  DEPOSIT_TRANSACTION_TYPES.includes(transaction?.type);

export const isDepositApplication = (payment) =>
  payment?.type === "SALES_DEPOSIT_APPLICATION";

const toRupiah = (value) => Math.round(Number(value) || 0);

// DPP Nilai Lain offered on a deposit: 11/12 of the amount, for an ordinary
// delivery taxed at PPN 12%.
export const DEPOSIT_OTHER_TAX_BASE = { numerator: 11, denominator: 12 };

/**
 * True for no fraction at all, or one that leaves DPP as it is (1/1, which is
 * what the API reports for a deposit saved without DPP Nilai Lain).
 */
export const isWholeFraction = (fraction) =>
  !fraction?.numerator ||
  !fraction?.denominator ||
  Number(fraction.numerator) === Number(fraction.denominator);

/**
 * What the customer actually hands over for a deposit of `taxBase` (DPP).
 *
 * Every rupiah figure is whole, like the API's. A withheld tax (PPh 23) is kept
 * back by the customer, so it is listed with a positive amount and comes off
 * the total — the same sign convention as invoice taxes. A FIXED tax is one
 * absolute amount: a deposit has no quantity to multiply it by.
 *
 * `fraction` ({ numerator, denominator }) is DPP Nilai Lain: PPN and PPnBM are
 * charged on taxBase × numerator / denominator, while a withheld tax (and an
 * old tax with a negative rate) stays on the full amount.
 */
export const calculateDepositTotals = (taxBase, taxes = [], fraction) => {
  const base = toRupiah(taxBase);
  const otherTaxBase = isWholeFraction(fraction)
    ? base
    : toRupiah((base * fraction.numerator) / fraction.denominator);

  const lines = (taxes || []).filter(Boolean).map((tax) => {
    const computation = tax.computation || "PERCENTAGE";
    const rate = tax.amount ?? tax.rate ?? null;
    let amount = 0;
    if (computation === "PERCENTAGE" && rate) {
      const onFullAmount = tax.withholding || Number(rate) < 0;
      amount = toRupiah(
        ((onFullAmount ? base : otherTaxBase) * Number(rate)) / 100,
      );
    } else if (computation === "FIXED" && rate) {
      amount = toRupiah(rate);
    }
    return {
      id: tax.id,
      name: tax.name,
      computation,
      rate,
      withholding: Boolean(tax.withholding),
      amount,
    };
  });

  const added = lines.reduce(
    (sum, l) => (l.withholding ? sum : sum + l.amount),
    0,
  );
  const withheld = lines.reduce(
    (sum, l) => (l.withholding ? sum + l.amount : sum),
    0,
  );

  return {
    taxBase: base,
    otherTaxBase,
    taxes: lines,
    added,
    withheld,
    total: base + added - withheld,
  };
};

const time = (value) => {
  const t = new Date(value).getTime();
  return Number.isNaN(t) ? 0 : t;
};

/** Oldest first; the deposit number breaks a tie so the order is stable. */
export const sortDepositsOldestFirst = (deposits = []) =>
  [...(deposits || [])].sort(
    (a, b) =>
      time(a.createdAt) - time(b.createdAt) ||
      String(a.depositNumber || "").localeCompare(
        String(b.depositNumber || ""),
      ),
  );

/**
 * The default use of a customer's deposits against one balance: oldest first,
 * each up to what it has left, until the balance is covered.
 *
 * Returns `{ [depositID]: amount }` holding only deposits that get something;
 * one left out is unticked in the form.
 */
export const buildDefaultAllocations = (deposits = [], limit = 0) => {
  let left = Math.max(0, toRupiah(limit));
  const allocations = {};
  sortDepositsOldestFirst(deposits).forEach((deposit) => {
    if (left <= 0) return;
    const amount = Math.min(Math.max(0, toRupiah(deposit.remaining)), left);
    if (amount > 0) {
      allocations[deposit.id] = amount;
      left -= amount;
    }
  });
  return allocations;
};

export const sumAllocations = (allocations = {}) =>
  Object.values(allocations || {}).reduce(
    (sum, amount) => sum + (Number(amount) || 0),
    0,
  );

/**
 * What is still owed on a posted invoice, in the whole rupiah a deposit can be
 * applied in. useInvoiceTotals' settlementAmount, from the same
 * getInvoiceBalance, for code that needs it outside render (the moment
 * deposits or an invoice arrive).
 */
export const getInvoiceSettlement = (invoice) => {
  if (typeof invoice?.total !== "number") return 0;
  return getInvoiceBalance(invoice.total, invoice.payments?.items)
    .settlementAmount;
};

/**
 * Per-deposit problems with an edited allocation, keyed by deposit id, plus
 * `total` when the lot together is more than the balance it is applied to.
 * Returns an empty object when the allocation can be sent.
 */
export const validateAllocations = (
  allocations = {},
  deposits = [],
  limit = 0,
) => {
  const errors = {};
  const byID = new Map((deposits || []).map((d) => [d.id, d]));
  Object.entries(allocations || {}).forEach(([id, amount]) => {
    const value = Number(amount) || 0;
    const remaining = toRupiah(byID.get(id)?.remaining);
    if (value <= 0) errors[id] = "deposits.apply.errors.amountRequired";
    else if (value > remaining)
      errors[id] = "deposits.apply.errors.exceedsRemaining";
  });
  if (sumAllocations(allocations) > Math.max(0, toRupiah(limit))) {
    errors.total = "deposits.apply.errors.exceedsBalance";
  }
  return errors;
};

/**
 * The date an application is posted on: the invoice's own date, or the
 * deposit's when that is later. It cannot be chosen — anything else would
 * leave the receivable and the deposit both standing for a while.
 */
export const getDepositApplyDate = (invoiceDate, depositDate) => {
  const invoice = invoiceDate ? new Date(invoiceDate) : null;
  const deposit = depositDate ? new Date(depositDate) : null;
  if (!invoice) return deposit;
  if (!deposit) return invoice;
  return deposit > invoice ? deposit : invoice;
};

export const DEPOSIT_ERROR_CODES = [
  "DEPOSIT_USED",
  "DEPOSIT_AMOUNT",
  "DEPOSIT_PARTNER",
  "DEPOSIT_NOT_REFUNDABLE",
];

/**
 * The server's own sentence for a deposit business-rule rejection, or null for
 * any other error. As with HAS_PAYMENTS, `code` is what we branch on and
 * `message` is shown untouched.
 */
export const getDepositErrorMessage = (error) => {
  const body = getApiErrorBody(error);
  const code = body?.code ?? body?.error?.code;
  if (!DEPOSIT_ERROR_CODES.includes(code)) return null;
  const message = body.message ?? body.error?.message;
  return typeof message === "string" && message ? message : null;
};

/**
 * The one line shown next to a deposit action's button when it fails: the
 * server's own sentence for a business rule or a closed period, else the usual
 * validation messages, translated.
 */
export const getDepositActionError = (error, t) => {
  if (!error) return "";
  return (
    getDepositErrorMessage(error) ||
    getPeriodClosedError(error)?.message ||
    getHasPaymentsMessage(error) ||
    getErrorMessages(error, t).join(" ")
  );
};
